import { Controller } from "@hotwired/stimulus"

// apps--edit
//
// Handles app create/edit form: jQuery validation, AJAX submission with
// status-code routing, image preview, copy-to-clipboard, and Custom Identity
// Provider toggle. Used by both apps/edit.haml and apps/new.haml.
export default class extends Controller {
  static targets = [
    "fileInput",
    "previewImage",
    "fileName",
    "loading",
    "formErrors",
    "identityProviderSettings",
    "identityProviderInfo",
  ]

  static values = {
    appsUrl: { type: String, default: "/apps" },
  }

  connect() {
    this.$form = $(this.element)
    this.#addCustomValidators()
    this.#setupValidation()
  }

  disconnect() {
    if (this.$form) {
      const validator = this.$form.data("validator")
      if (validator) {
        validator.resetForm()
        this.$form.removeData("validator")
      }
      this.$form.find("label.error").remove()
    }
    this.$form = null
  }

  submit(event) {
    event.preventDefault()
    if (!this.$form.valid()) return

    this.#hideAlerts()
    this.#setLoading(true)

    const form = this.element
    const csrf = document.querySelector("meta[name='csrf-token']")?.content

    fetch(form.action, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrf || "",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: new FormData(form),
      credentials: "same-origin",
    })
      .then((response) => this.#handleResponse(response))
      .catch(() => this.#showError("Network error. Please try again."))
      .finally(() => this.#setLoading(false))
  }

  selectField(event) {
    event.currentTarget.select()
  }

  copyToClipboard(event) {
    event.preventDefault()
    const inputId = event.currentTarget.id.substring(2)
    const input = document.getElementById(inputId)
    if (input) {
      input.select()
      document.execCommand("copy")
    }
  }

  chooseFile(event) {
    event.preventDefault()
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  previewFile = () => {
    const file = this.fileInputTarget.files[0]
    if (!file) return

    if (file.size > 1048576) {
      alert(`Image is too large (current: ${file.size}, max: 1MB)`)
      this.fileInputTarget.value = ""
      if (this.hasFileNameTarget) this.fileNameTarget.value = ""
      return
    }

    const reader = new FileReader()
    const image = new Image()

    reader.readAsDataURL(file)
    reader.onload = (e) => {
      image.src = e.target.result
      image.onload = () => {
        if (this.hasPreviewImageTarget) {
          this.previewImageTarget.src = image.src
        }
      }
      image.onerror = () => {
        alert(`Invalid file type: ${file.type}`)
        this.fileInputTarget.value = ""
        if (this.hasFileNameTarget) this.fileNameTarget.value = ""
      }
    }
  }

  toggleIdentityProvider() {
    if (this.hasIdentityProviderSettingsTarget) {
      $(this.identityProviderSettingsTarget).fadeToggle("slow", "linear")
    }
    if (this.hasIdentityProviderInfoTarget) {
      $(this.identityProviderInfoTarget).fadeToggle("slow", "linear")
    }
  }

  deleteApp(event) {
    event.preventDefault()

    const message = event.currentTarget.dataset.confirmMessage
    if (message && !window.confirm(message)) return

    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    fetch(event.currentTarget.href, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrf || "",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    }).then((response) => {
      if (response.ok) window.location.href = "/"
    })
  }

  // -- private --

  #addCustomValidators() {
    if (!$.validator) return

    if (!$.validator.methods.regex) {
      $.validator.addMethod("regex", function (value, element, regexp) {
        return this.optional(element) || regexp.test(value)
      }, "Bad format")
    }

    if (!$.validator.methods.isValidJSON) {
      $.validator.addMethod("isValidJSON", (value) => {
        if (value === "") return true
        try { JSON.parse(value); return true } catch (_) { return false }
      }, "Wrong parameters format.")
    }

    if (!$.validator.methods.customParamsFilled) {
      $.validator.addMethod("customParamsFilled", (value) => {
        if (value === "" && $("#application_custom_identity_provider_on").val() === "1") {
          return false
        }
        return true
      }, "This field is required.")
    }
  }

  #setupValidation() {
    const EXTRA_UTF = "\u007f-\u2115"
    const allnumG = new RegExp(`^[${EXTRA_UTF}a-zA-Z0-9][-${EXTRA_UTF}a-zA-Z0-9 ]*$`)
    const HEX = /^[0-9A-Fa-f]+$/

    this.$form.validate({
      onsubmit: false,
      rules: {
        "application[title]": { required: true, maxlength: 30, regex: allnumG },
        "application[url]": { required: false, minlength: 3, maxlength: 50, url: true },
        "application[type]": { required: true },
        "facebook[key]": { required: "#facebook_secret:filled", regex: HEX, maxlength: 40 },
        "facebook[secret]": { required: "#facebook_key:filled", regex: allnumG, maxlength: 40 },
        "custom_provider[auth_url]": {
          required: "#auth_response_params:filled, #auth_request_params:filled, #auth_request_headers:filled",
          customParamsFilled: { data: $("#auth_url") },
          regex: /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/,
        },
        "custom_provider[auth_response_params]": {
          required: "#auth_url:filled, #auth_request_params:filled, #auth_request_headers:filled",
          isValidJSON: { data: $("#auth_response_params") },
        },
        "custom_provider[auth_request_params]": {
          required: "#auth_request_headers:blank",
          isValidJSON: { data: $("#auth_request_params") },
        },
        "custom_provider[auth_request_headers]": {
          isValidJSON: { data: $("#auth_request_headers") },
        },
      },
      messages: {
        "application[title]": { regex: "Bad format" },
      },
    })
  }

  async #handleResponse(response) {
    let payload
    try { payload = await response.json() } catch (_) { payload = null }

    switch (response.status) {
      case 200:
        this.#showSuccess("Application has been updated!")
        this.#updateTitleAndAvatar()
        break
      case 201:
        if (payload?.app_id) {
          window.location.href = `${this.appsUrlValue}/${payload.app_id}/edit`
        }
        break
      case 204:
        window.location.href = this.appsUrlValue
        break
      default:
        if (payload?.errors) {
          this.#showError(payload.errors)
        } else {
          this.#showError("Something went wrong.")
        }
    }
  }

  #updateTitleAndAvatar() {
    const bigAvatar = document.querySelector(".app-image")
    const smallAvatarSrc = this.hasPreviewImageTarget ? this.previewImageTarget.src : null
    const newTitle = this.element.querySelector("#application_title")?.value

    if (bigAvatar && smallAvatarSrc && bigAvatar.src !== smallAvatarSrc) {
      bigAvatar.src = smallAvatarSrc
    }

    const appListTitle = document.querySelector("#app-selector .title")
    if (appListTitle && newTitle && appListTitle.innerHTML !== newTitle) {
      appListTitle.innerHTML = newTitle
      const breadcrumb = document.querySelector(".breadcrumb li:last-child")
      if (breadcrumb) breadcrumb.innerHTML = newTitle
    }
  }

  #hideAlerts() {
    if (this.hasFormErrorsTarget) {
      this.formErrorsTarget.innerHTML = ""
    }
    this.element.querySelectorAll(".form-results-success").forEach((el) => {
      el.style.display = "none"
    })
  }

  #showSuccess(message) {
    const el = this.element.querySelector(".form-results-success")
    if (!el) return
    const content = el.querySelector(".content") || el
    content.innerHTML = message
    el.classList.remove("tmp-hide")
    el.style.display = ""
    el.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  #showError(message) {
    if (!this.hasFormErrorsTarget) return

    this.formErrorsTarget.innerHTML = message
    this.formErrorsTarget.querySelectorAll(".tmp-hide, .form-results-errors").forEach((el) => {
      el.classList.remove("tmp-hide")
      el.style.display = ""
    })
    this.formErrorsTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  #setLoading(show) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.style.display = show ? "" : "none"
    }
  }
}
